import React from 'react';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { InputField, TextField } from '@getgo/chameleon-web-react-wrapper';
import { Typography, Button, TextArea, AlertV2, IconButton } from '@getgo/chameleon-web-react-wrapper';
import { AddOutlinedIcon, TrashOutlinedIcon } from '@getgo/chameleon-icons/react';
import st from 'core/shared-translations';
import classes from './client-details-tab.scss';

const t = defineMessages({
  'client-edit.field.owners': {
    id: 'client-edit.field.owners',
    defaultMessage: 'Owner',
    description: 'Label for the Owner field',
  },
  'client-edit.field.owners.placeholder': {
    id: 'client-edit.field.owners.placeholder',
    defaultMessage: "Enter owner's email",
    description: 'Placeholder for the Owner field',
  },
  'client-edit.field.owners.add.buttonText': {
    id: 'client-edit.field.owners.add.buttonText',
    defaultMessage: 'Add owner',
    description: 'Label for the Add owner button',
  },
  'client-edit.field.owners.current.label': {
    id: 'client-edit.field.owners.current.label',
    defaultMessage: 'Current owners',
    description: 'Label for the current owners section',
  },
  'client-edit.field.owners.none.message': {
    id: 'client-edit.field.owners.none.message',
    defaultMessage: 'No owners added',
    description: 'Message when no owners are added',
  },
  'client-edit.validation.duplicate-owner-email': {
    id: 'client-edit.validation.duplicate-owner-email',
    defaultMessage: "Owner email already exists.",
    description: 'Error message to be displayed when the owner email is duplicate'
  },
});

const ClientOwnersTab = ({
  client,
  onChange,
  onBlur,
  errors,
  addOwnerEmail,
  onOwnerEmailChange,
  onOwnerEmailBlur,
  onAddOwner,
  isOwnerEmailValid,
  onDeleteOwner,
  canWrite,
  owners
}) => {
  const intl = useIntl();
  return (
    <React.Fragment>
      <Box className={classes["ownersContainer"]}>
        <Box className={classes["ownersTabSpacing"]}>
          <TextField
            id="addOwnerEmail"
            name="addOwnerEmail"
            role="textbox"
            title={<FormattedMessage {...t["client-edit.field.owners"]} />}
            value={addOwnerEmail || ''}
            onChange={onOwnerEmailChange}
            placeholder={intl.formatMessage(t["client-edit.field.owners.placeholder"])}
            onBlur={onOwnerEmailBlur}
            error={!!errors.addOwnerEmail}
            disabled={!canWrite}
            fullwidth
            helperText={
              (errors.addOwnerEmail &&
                intl.formatMessage(st[errors.addOwnerEmail] || t[errors.addOwnerEmail]))
            }
          >
            <FormattedMessage {...t["client-edit.field.owners"]} />
          </TextField>
          <Button
            data-testid="add-owners-button"
            className={isOwnerEmailValid ? classes["addRedirectUriButton"] : ""}
            onClick={onAddOwner}
            style={{ marginTop: "23px" }}
            disabled={!isOwnerEmailValid || !canWrite}
            variant="secondary"
            leadingIcon={<AddOutlinedIcon color={`${isOwnerEmailValid ? "#2C72DE" : ""}`} />}>
            <FormattedMessage {...t['client-edit.field.owners.add.buttonText']} />
          </Button>
        </Box>
        <Box>
          <Typography variant="caption-medium" color="type-color-secondary">
            <FormattedMessage {...t['client-edit.field.owners.current.label']} />
          </Typography>
          {((client?.owners && client.owners.length > 0) || (owners && owners.length > 0)) &&
            (client?.owners?.length ? client.owners : owners).map((owner, index) => (
              <Box key={`owner-${index}`}>
                <Typography variant="body-small">
                  {owner.email}
                </Typography>
                {canWrite && (
                  <IconButton
                    data-testid={`delete-owner-${index}`}
                    onClick={onDeleteOwner(index)}>
                    <TrashOutlinedIcon color="#2C72DE" />
                  </IconButton>
                )}
              </Box>
            ))}
          {!((client?.owners && client.owners.length > 0) || (owners && owners.length > 0)) && (
            <Typography variant="body-small" color="type-color-secondary">
              <FormattedMessage {...t['client-edit.field.owners.none.message']} />
            </Typography>
          )}
        </Box>
      </Box>
    </React.Fragment>
  );
};

ClientOwnersTab.propTypes = {
  client: PropTypes.object,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  addOwnerEmail: PropTypes.string,
  onOwnerEmailChange: PropTypes.func.isRequired,
  onOwnerEmailBlur: PropTypes.func.isRequired,
  onAddOwner: PropTypes.func.isRequired,
  isOwnerEmailValid: PropTypes.bool.isRequired,
  errors: PropTypes.object.isRequired,
  onDeleteOwner: PropTypes.func.isRequired,
  canWrite: PropTypes.bool.isRequired,
  owners: PropTypes.array,
};

export default ClientOwnersTab;
